import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import { Override } from '../../../utils/typescript'

export const DEFAULT_MODEL = 'rws-llm'
export const DEFAULT_CONTEXT_LABEL = 'Context'
export const DEFAULT_PLACEHOLDER = 'Ask the agent…'

/**
 * Kept `as const` so the widget props can be derived from it.
 *
 * `formGroup` collects options into a labelled fieldset, in the order the groups first appear.
 * Members of a group are kept together here so the source reads in the same order as the dialog.
 */
export const configOptions = [
  {
    formGroup: 'Model',
    name: 'model',
    type: 'text',
    label: `Model id, e.g. "${DEFAULT_MODEL}". Whatever the configured endpoint answers to.`,
    defaultValue: DEFAULT_MODEL,
  },
  {
    formGroup: 'Model',
    name: 'answerScope',
    type: 'textarea',
    label:
      'Restrict the agent to one subject, named in a few words, e.g. "the assets, areas and ' +
      'measures held in this repository". Everything else is declined in one sentence. Leave ' +
      'empty and the agent answers whatever it is asked.',
  },
  {
    formGroup: 'Model',
    name: 'extraSystemPrompt',
    type: 'textarea',
    label:
      'Extra instructions, added after the agent\'s own. The agent already tells the model how to ' +
      'use the tools and how to read the results, so this is for domain guidance rather than for ' +
      'repeating that.',
  },

  {
    formGroup: 'Conversation',
    name: 'welcomeMessage',
    type: 'textarea',
    label: 'Text shown in the empty chat, before the first question.',
  },
  {
    formGroup: 'Conversation',
    name: 'placeholder',
    type: 'text',
    label: `Placeholder in the input field (default: "${DEFAULT_PLACEHOLDER}").`,
  },
  {
    formGroup: 'Conversation',
    name: 'showToolCalls',
    type: 'boolean',
    label:
      'Show the agent\'s tool calls (SPARQL queries) in collapsible cards, so an answer can be ' +
      'checked against what was actually asked. Default: on.',
  },
  {
    formGroup: 'Conversation',
    name: 'showThinking',
    type: 'boolean',
    label:
      'Show the reasoning the model produces before it answers, in a collapsible "Thinking" ' +
      'panel. Turn it off and the reasoning is replaced by a "Thinking..." indicator while the ' +
      'answer is being worked out. Default: on.',
  },

  {
    formGroup: 'Avatars',
    name: 'showAvatars',
    type: 'boolean',
    label: 'Show an avatar beside each message. Default: on.',
  },
  {
    formGroup: 'Avatars',
    name: 'assistantAvatarUrl',
    type: 'text',
    label:
      'Image for the agent: a URL, a data: URI, or bare base64. Leave empty for the default ' +
      'icon. Anything that does not load falls back to the icon rather than showing a broken ' +
      'image. Base64 is stored inline in the app config, so keep it small.',
  },
  {
    formGroup: 'Avatars',
    name: 'assistantInitials',
    type: 'text',
    label: 'One or two letters for the agent, used when no image URL is set.',
  },
  {
    formGroup: 'Avatars',
    name: 'userAvatarUrl',
    type: 'text',
    label:
      'Image for the person asking: a URL, a data: URI, or bare base64. Leave empty for the ' +
      'default icon.',
  },
  {
    formGroup: 'Avatars',
    name: 'userInitials',
    type: 'text',
    label: 'One or two letters for the person asking, used when no image URL is set.',
  },

  {
    formGroup: 'Context sent with each question',
    name: 'contextTopic',
    type: 'text',
    label:
      'Topic (app variable) whose current value is attached to every question, ' +
      'so "tell me about this" works on the current selection.',
  },
  {
    formGroup: 'Context sent with each question',
    name: 'contextLabel',
    type: 'text',
    label: `How the attached context is labelled for the model (default: "${DEFAULT_CONTEXT_LABEL}").`,
  },

  {
    formGroup: 'Topics published by the answer',
    name: 'answerTopic',
    type: 'text',
    label: 'Topic to publish each completed answer to, so other widgets can react to it.',
  },
  {
    formGroup: 'Topics published by the answer',
    name: 'iriTopic',
    type: 'text',
    label: 'Topic to publish the first IRI found in each answer to (empty when the answer has none).',
  },
] as const

type RawAgentChatConfig = ConfigurablePropsFromDescription<typeof configOptions>

/* 'textarea' is `never` in the shared type map; override it locally, as CodeWidget does. */
export type AgentChatConfigOptions = Override<RawAgentChatConfig, {
  answerScope?: string | null
  extraSystemPrompt?: string | null
  welcomeMessage?: string | null
}>
